/**
 * Die GEZEICHNETE Hauptnavigation — Seitenleiste (ab lg), mobile Fußleiste und mobiles
 * Vollbild-Menü (`MobileNavDrawer`, Nav-Umbau 28.08.2026).
 *
 * Warum eine eigene Datei: `navigation.ts` beweist nur die Liste, nicht das Bild. P0
 * (docs/KONZEPT-KATALOG-NAVIGATION.md § 4.1) verlangt aber genau das Bild — dass „Karten" und
 * „Pakete" unter „Katalog" TATSÄCHLICH dastehen und anklickbar sind. Aus `AppShell.tsx` heraus
 * wäre das nur mit Stack Auth, Copilot und Suche zu rendern; hier reicht ein Router.
 *
 * Alle drei Fassungen stehen bewusst nebeneinander in dieser Datei: eine Navigation, die nur am
 * Schreibtisch funktioniert, ist keine — Antoine (28.08.2026): „Auf Mobil soll alles bedienbar
 * sein, wie auf dem Desktop. Keine abgespeckte Version." `MobileNavDrawer` rendert deshalb
 * dieselbe `SeitenNavigation` (mit `collapsed={false}`) statt einer eigenen, potenziell
 * abweichenden Kopie der Liste.
 */
import { Fragment, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Icon } from "./components";
import { NAV, MOBILE_NAV, type NavEintrag } from "./navigation";
import { useSeitenKontext } from "../lib/rueckweg";
import { useOverlayOpenSignal } from "../hooks/useOverlayOpen";

// Dunkelbraune Sidebar (#4A2511): aktiver Eintrag = Akzent-Orange, sonst helles Cremeweiß.
// `collapsed` (Rail): Icons zentriert, Labels ausgeblendet, kompaktes Padding.
export function navLinkClass(isActive: boolean, collapsed: boolean): string {
  return [
    "relative flex items-center rounded-[9px] font-body-sm text-body-sm font-medium transition-colors",
    collapsed ? "justify-center px-0 py-2" : "gap-3 px-3 py-2",
    isActive
      ? "bg-primary text-on-primary font-semibold"
      : "text-[#EBE3D4] hover:bg-white/5 hover:text-inverse-on-surface",
  ].join(" ");
}

/** Ein anklickbares Ziel der Seitenleiste (Blatt oder Untereintrag). */
function Ziel({
  item,
  collapsed,
  wartetAufDich,
  posteingangOhneVorgang,
  gedaechtnisNeu,
  onNavigate,
}: {
  item: NavEintrag;
  collapsed: boolean;
  wartetAufDich: number;
  /** Phase C.2 WP-E (Postfach-Zuordnung): Zahl der unzugeordneten Absender-Gruppen ("Ohne
   *  Vorgang n" in posteingang.tsx) - dieselbe Zaehlwahrheit wie die Bell-Meldung
   *  (NotificationsBell.tsx), aus `GET /api/inbox/zaehler` (AppShell.tsx). */
  posteingangOhneVorgang: number;
  /** Lern-Übersicht (Mockup UXqy6s3qxdztdoVQKAKhi9): Einträge seit dem letzten Besuch der Seite
   *  Gedächtnis, aus `GET /api/gedaechtnis/neu-anzahl` (AppShell.tsx). Erscheint als „n neu“. */
  gedaechtnisNeu: number;
  /** `MobileNavDrawer`: schließt das Menü nach einem Klick — die Seitenleiste braucht das nicht. */
  onNavigate?: () => void;
}) {
  // Phase C.2 WP-E: derselbe hartcodierte Zielpfad-Schalter wie bisher (kein generisches
  // `badges: Record<string, number>` in `navigation.ts` - siehe Kommentar dort), nur um ein
  // zweites Ziel erweitert.
  const badgeCount =
    item.to === "/anfragen" ? wartetAufDich
    : item.to === "/posteingang" ? posteingangOhneVorgang
    : item.to === "/gedaechtnis" ? gedaechtnisNeu
    : 0;
  // Mockup Lern-Übersicht: am Gedächtnis steht „7 neu“ als Akzent-Pille, nicht die blasse Zahl.
  const neuPille = item.to === "/gedaechtnis";
  // Punkt 115 (G4): auf der Vorgangsseite ist die Liste markiert, in der der Fall steht.
  const kontextAktiv = useSeitenKontext()?.bereich === item.to;
  return (
    <NavLink
      to={item.to!}
      end={item.end}
      onClick={onNavigate}
      // Immer gesetzt (nicht nur eingeklappt): so erscheint das Label als nativer Tooltip
      // auch, wenn das Menü nur schmal gezogen ist und die Beschriftung abgeschnitten wird.
      title={item.label}
      className={({ isActive }) => navLinkClass(isActive || kontextAktiv, collapsed)}
    >
      {({ isActive: routeAktiv }) => {
        const isActive = routeAktiv || kontextAktiv;
        return (
        <>
          <Icon name={item.icon} className="text-[20px]" />
          {!collapsed && <span className="flex-1">{item.label}</span>}
          {badgeCount > 0 &&
            (collapsed ? (
              // Rail: Badge als kompakter Punkt (Ton je nach aktiv/inaktiv sichtbar).
              <span
                aria-hidden
                className={`absolute right-1 top-1 h-2 w-2 rounded-full ring-2 ring-[#4A2511] ${
                  isActive ? "bg-on-primary" : "bg-primary"
                }`}
              />
            ) : (
              neuPille ? (
                <span
                  className={`rounded-full px-[7px] py-px text-[10px] font-semibold ${
                    isActive ? "bg-on-primary text-primary" : "bg-primary text-on-primary"
                  }`}
                >
                  {badgeCount} neu
                </span>
              ) : (
                <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10.5px] font-semibold text-inverse-on-surface">
                  {badgeCount}
                </span>
              )
            ))}
        </>
        );
      }}
    </NavLink>
  );
}

/** Die Seitenleiste (ab lg sichtbar) — von `MobileNavDrawer` mit `collapsed=false` wiederverwendet,
 *  damit beide exakt dieselbe Struktur/Gruppierung zeigen (kein zweiter, potenziell abweichender
 *  Rechenweg). `onNavigate` schließt nur im Drawer-Kontext etwas — in der Seitenleiste bleibt es
 *  ungesetzt. */
export function SeitenNavigation({
  collapsed,
  wartetAufDich,
  posteingangOhneVorgang,
  gedaechtnisNeu = 0,
  onNavigate,
}: {
  collapsed: boolean;
  wartetAufDich: number;
  /** Phase C.2 WP-E: siehe `Ziel` oben. */
  posteingangOhneVorgang: number;
  /** Siehe `Ziel` oben. */
  gedaechtnisNeu?: number;
  onNavigate?: () => void;
}) {
  let vorherigeGruppe: string | undefined;
  return (
    <ul className="flex-1 space-y-0.5 overflow-y-auto">
      {NAV.map((item) => {
        // Rein visuelle Zwischenüberschrift (Nav-Umbau 28.08.2026, s. navigation.ts): nur bei
        // einem WECHSEL des `gruppe`-Werts zum Vorgänger, nie bei zwei benachbarten ohne Gruppe
        // (z.B. „Übersicht" direkt gefolgt von der ersten "Vertrieb"-Zeile zaehlt als Wechsel,
        // "undefined" selbst bekommt aber nie eine Überschrift).
        const neueGruppe = !collapsed && item.gruppe != null && item.gruppe !== vorherigeGruppe;
        vorherigeGruppe = item.gruppe;
        const kopf = neueGruppe ? (
          <div
            key={`gruppe-${item.gruppe}`}
            className="px-3 pb-0.5 pt-3 font-label-md text-[11px] font-semibold uppercase tracking-wider text-[#EBE3D4]/60"
          >
            {item.gruppe}
          </div>
        ) : null;
        return item.kinder ? (
          <Fragment key={item.label}>
            {kopf}
            <li>
              {/* § 8 (Red Team): „Katalog" ist Überschrift, kein Link. Wäre es klickbar, müsste es
                  zwangsweise auf einen der beiden Untereinträge umleiten — genau die Mehrdeutigkeit,
                  die das Konzept beseitigen soll. Eingeklappt (Rail) entfällt die Überschrift; die
                  Untereinträge stehen dort mit eigenem Icon, damit auch die Rail vollständig bleibt. */}
              {!collapsed && (
                <div
                  id={`nav-gruppe-${item.label}`}
                  className="flex items-center gap-3 px-3 pb-0.5 pt-2 font-label-md text-[11px] font-semibold uppercase tracking-wider text-[#EBE3D4]/60"
                >
                  <Icon name={item.icon} className="text-[20px]" />
                  <span className="flex-1">{item.label}</span>
                </div>
              )}
              <ul
                aria-labelledby={collapsed ? undefined : `nav-gruppe-${item.label}`}
                aria-label={collapsed ? item.label : undefined}
                className={collapsed ? "space-y-0.5" : "space-y-0.5 pl-3"}
              >
                {item.kinder.map((kind) => (
                  <li key={kind.to}>
                    <Ziel
                      item={kind}
                      collapsed={collapsed}
                      wartetAufDich={wartetAufDich}
                      posteingangOhneVorgang={posteingangOhneVorgang}
                      gedaechtnisNeu={gedaechtnisNeu}
                      onNavigate={onNavigate}
                    />
                  </li>
                ))}
              </ul>
            </li>
          </Fragment>
        ) : (
          <Fragment key={item.to}>
            {kopf}
            <li>
              <Ziel
                item={item}
                collapsed={collapsed}
                wartetAufDich={wartetAufDich}
                posteingangOhneVorgang={posteingangOhneVorgang}
                gedaechtnisNeu={gedaechtnisNeu}
                onNavigate={onNavigate}
              />
            </li>
          </Fragment>
        );
      })}
    </ul>
  );
}

/** Mobile Fußleiste (dunkler Karten-Ton, iOS-Safe-Area) — nur die fünf Wege aus `MOBILE_NAV`.
 *  Der Hamburger-Knopf, der `MobileNavDrawer` mit ALLEN 17 Zielen öffnet, sitzt bewusst NICHT
 *  hier als sechstes Icon: ein Messversuch (28.08.2026) zeigte, dass sechs Icons bei 390px
 *  Breite die Leiste ueberlaufen ("Veranstaltungen"/"Einstellungen" allein sind schon relativ
 *  breit) — das letzte Icon landete ausserhalb des sichtbaren Viewports. Der Knopf sitzt
 *  stattdessen in der mobilen Topbar (`AppShell.tsx`, dort ist Platz), diese Leiste bleibt
 *  unveraendert. */
export function MobileNavigation({ wartetAufDich }: { wartetAufDich: number }) {
  const kontextBereich = useSeitenKontext()?.bereich;
  return (
    <nav
      aria-label="Hauptnavigation (mobil)"
      className="fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-outline-variant/30 bg-surface-container-lowest pt-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] dark:border-white/10 dark:bg-night-card lg:hidden"
    >
      {MOBILE_NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to!}
          end={item.end}
          className={({ isActive }) =>
            [
              "relative flex flex-col items-center gap-0.5 rounded-lg px-3 py-1 font-label-md text-[10px]",
              isActive || kontextBereich === item.to ? "text-primary" : "text-secondary dark:text-secondary-fixed-dim",
            ].join(" ")
          }
        >
          <Icon name={item.icon} className="text-xl" />
          {item.label}
          {item.to === "/anfragen" && wartetAufDich > 0 && (
            <span className="absolute -top-1 right-1 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-primary px-1 text-[8.5px] font-bold text-on-primary">
              {wartetAufDich}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Vollbild-Ausklapp-Menü fürs Telefon (Nav-Umbau 28.08.2026): zeigt DIESELBE gruppierte Liste wie
 * die Desktop-Seitenleiste (`SeitenNavigation collapsed={false}`), inkl. „Katalog"-Kinder — alle
 * 17 Ziele, nicht nur die fünf der Fußleiste. Schließt sich selbst nach einem Klick auf ein Ziel
 * (`onNavigate={onClose}`), per Escape-Taste oder per Klick auf den Hintergrund/„Schließen".
 */
export function MobileNavDrawer({
  open,
  onClose,
  wartetAufDich,
  posteingangOhneVorgang,
  gedaechtnisNeu = 0,
}: {
  open: boolean;
  onClose: () => void;
  wartetAufDich: number;
  /** Phase C.2 WP-E: siehe `Ziel` oben - wirkt hier ueber die wiederverwendete `SeitenNavigation`. */
  posteingangOhneVorgang: number;
  /** Siehe `Ziel` oben. */
  gedaechtnisNeu?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const aufEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", aufEscape);
    return () => document.removeEventListener("keydown", aufEscape);
  }, [open, onClose]);

  useOverlayOpenSignal(open);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] lg:hidden">
      <button
        type="button"
        aria-label="Menü schließen"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <nav
        aria-label="Hauptnavigation (mobil, vollständig)"
        className="absolute inset-y-0 left-0 flex w-[82vw] max-w-80 flex-col bg-[#4A2511] pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-xl"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <span className="font-headline-sm text-headline-sm font-bold text-inverse-on-surface">MAESTRO</span>
          <button
            type="button"
            aria-label="Menü schließen"
            onClick={onClose}
            className="rounded-full p-2 text-[#EBE3D4] hover:bg-white/10"
          >
            <Icon name="close" className="text-xl" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          <SeitenNavigation
            collapsed={false}
            wartetAufDich={wartetAufDich}
            posteingangOhneVorgang={posteingangOhneVorgang}
            gedaechtnisNeu={gedaechtnisNeu}
            onNavigate={onClose}
          />
        </div>
      </nav>
    </div>
  );
}
